import { useEffect, useState } from 'react';
import { TableCell } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  SimpleTable,
  TableWrapperFooter,
  Pagination,
  Chip,
  TableIconsCell,
  TableDots,
  invoicePaidModal,
  invoiceDeleteModal,
  invoiceCreateModal,
  Typography,
} from '../../design-system';
import { initialInvoiceGetAllQuery, useInvoicesQuery, usePaginationState } from '../../hooks';
import { useStore } from '../../context';
import {
  CURRENCIES,
  InvoiceModeToChipColors,
  InvoiceStatusToChipColors,
  InvoiceModeToChipLabels,
  InvoiceTypeToChipLabels,
  TABLE,
  InvoiceStatusToChipLabels,
  InvoiceTypeToChipColors,
} from '../../const';
import { getInvoiceOptions, maskPhoneNumber, momentTextFormat, showToRoles } from '../../utils';
import { i18n } from '../../i18n';
import { CurrencyCode } from '../../types';
import styled from 'styled-components';

const InvoicesViewSubHeaderBase = styled.div`
  display: grid;
  grid-auto-columns: max-content;
  grid-auto-flow: column;
  gap: 1.2rem;
`;

export const InvoicesSubHeader = ({ invoice }: any) => {
  return (
    <InvoicesViewSubHeaderBase>
      <Chip
        size={'small'}
        label={maskPhoneNumber(String(invoice?.clientYard?.yard?.phoneNumber))}
        color={'blue'}
      />
      <Typography variant={'textM'}>{i18n.t('label.phoneNumber')}</Typography>
    </InvoicesViewSubHeaderBase>
  );
};

export const InvoicesBody = ({ clientYardId, setTotalCount, setName, setInvoice }: any) => {
  const navigate = useNavigate();
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const [openDotsMenuIndex, setDotsMenuIndex] = useState<number | null>(null);
  const { page, take, skip, handlePageChange } = usePaginationState('invoices');

  const {
    data = initialInvoiceGetAllQuery,
    isFetching,
    refetch,
  } = useInvoicesQuery({
    clientYardId,
    take,
    skip,
  });

  useEffect(() => {
    if (isFetching) return;

    setInvoice(data.data[0]);
    setTotalCount(data.totalCount || 0);

    invoiceCreateModal.createMode = data.createMode;
    invoiceCreateModal.minDate = data.minDate;

    const clientYard = data.clientYard;
    if (!clientYard) return;

    if (showToRoles('adminRead', user)) {
      setName(`${clientYard.yard.name} <-> ${clientYard.client.code}`);
    } else {
      setName(clientYard.yard.name);
    }
  }, [isFetching, data.totalCount, setTotalCount, setName, data.data, user]);

  const handleDotsToggle = (i: number) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);
    setDotsMenuIndex(null);
  };

  const handleDotsOptionSelect = (invoice: any, val: string) => {
    setDotsMenuIndex(null);

    if (val === 'paid') {
      invoicePaidModal.invoiceId = invoice.id;
      invoicePaidModal.invoiceNumber = invoice.invoiceNumber;
      invoicePaidModal.submitClick = () => {
        refetch && refetch();
      };
      invoicePaidModal.open();
      return;
    }

    if (val === 'delete') {
      invoiceDeleteModal.invoiceId = invoice.id;
      invoiceDeleteModal.invoiceNumber = invoice.invoiceNumber;
      invoiceDeleteModal.submitClick = () => {
        refetch && refetch();
      };
      invoiceDeleteModal.open();
    }
  };

  const handleRowClick = (invoice: any) => {
    if (openDotsMenuIndex !== null) return;
    navigate(`/dashboard/invoices/view/${invoice.id}`);
  };

  return (
    <SimpleTable
      data={data}
      tableRowProps={(invoice, i: number) => ({
        key: invoice.id || i,
        onClick: () => handleRowClick(invoice),
      })}
      columns={[
        {
          column: 1,
          headText: () => i18n.t('label.actions'),
          body: (invoice, i: number) => (
            <TableIconsCell component={'th'} align={'left'}>
              {!!getInvoiceOptions(invoice, user).length && (
                <TableDots
                  open={openDotsMenuIndex === i}
                  onToggle={() => handleDotsToggle(i)}
                  options={getInvoiceOptions(invoice, user)}
                  onSelect={(val) => {
                    handleDotsOptionSelect(invoice, val);
                  }}
                />
              )}
            </TableIconsCell>
          ),
          isHidden: () => showToRoles('client', user),
        },
        {
          column: 1,
          headText: () => i18n.t('label.order'),
          body: (invoice) => (
            <TableCell component={'th'} align={'left'}>
              {invoice.invoiceNumber}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.mode'),
          body: (invoice) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={InvoiceModeToChipLabels[invoice.mode]}
                color={InvoiceModeToChipColors[invoice.mode]}
              />
            </TableCell>
          ),
          isHidden: () => showToRoles('client', user),
        },
        {
          headText: () => i18n.t('label.type'),
          body: (invoice) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={InvoiceTypeToChipLabels[invoice.type]}
                color={InvoiceTypeToChipColors[invoice.type]}
              />
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.status'),
          body: (invoice) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={InvoiceStatusToChipLabels[invoice.status]}
                color={InvoiceStatusToChipColors[invoice.status]}
              />
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.periodStart'),
          body: (invoice) => (
            <TableCell align={'left'}>{momentTextFormat(invoice.periodStart, user)}</TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.periodEnd'),
          body: (invoice) => (
            <TableCell align={'left'}>{momentTextFormat(invoice.periodEnd, user)}</TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.price'),
          body: (invoice) => (
            <TableCell align={'left'}>
              {invoice.totalAmount}
              {CURRENCIES[invoice.currencyCode as CurrencyCode]?.symbol}
            </TableCell>
          ),
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handlePageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
